import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Autocomplete,
  Snackbar,
  Alert,
  Grid,
  Chip,
  InputAdornment,
  Tooltip,
  Card,
  CardContent,
  Divider,
  Badge,
  FormControlLabel,
  Checkbox,
  Switch,
  Tabs,
  Tab,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  WarningAmber as WarningIcon,
  Category as CategoryIcon,
  Inventory2 as InventoryIcon,
  PriceCheck as PriceCheckIcon,
  Receipt as ReceiptIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { getTenantDataService } from '../services/tenant-data-service';
import { useTenantMigration } from '../hooks/useTenantMigration';
import { InventoryItem, InventoryCategory, GST_RATES, UNITS, StockFilter, INVENTORY_CATEGORIES, POSReturnRecord } from '../types/inventory';
import { Purchase } from '../types/purchase';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AnimatedPage from '../components/AnimatedPage';
import { Wand2, Printer, Search as SearchIconLucide } from 'lucide-react';
import BarcodeSticker from '../components/inventory/BarcodeSticker';

const Inventory: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const isCashier = userProfile?.role === 'cashier';
  // Tenant-aware data service: uses DynamoDB for staff, IndexedDB for admin
  const db = React.useMemo(
    () => getTenantDataService(currentUser, userProfile),
    [currentUser?.uid, userProfile?.tenantId] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // One-time migration: copy local IndexedDB → DynamoDB on first cloud login
  const migration = useTenantMigration(currentUser?.uid, db);
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [posReturns, setPosReturns] = useState<POSReturnRecord[]>([]);
  const [categories, setCategories] = useState<InventoryCategory[]>([]);
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [totalStockValue, setTotalStockValue] = useState(0);
  const [totalDamagedValue, setTotalDamagedValue] = useState(0);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [newCategory, setNewCategory] = useState<Partial<InventoryCategory>>({
    name: '',
    description: '',
  });
  const [filters, setFilters] = useState<StockFilter>({
    category: null,
    lowStock: false,
    dateRange: [null, null],
    priceRange: [null, null],
  });
  const [newItem, setNewItem] = useState<Partial<InventoryItem>>({
    name: '',
    description: '',
    skuWithUnit: '',
    category: '',
    purchasePrice: 0,
    sellingPrice: 0,
    gstRate: 0,
    hsnCode: '',
    stockQuantity: 0,
    minStockLevel: 0,
    purchaseId: '',
    barcode: '',
  });
  const [printingBarcode, setPrintingBarcode] = useState<InventoryItem | null>(null);
  const [printingQuantity, setPrintingQuantity] = useState(1);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [isBarcodeGenerated, setIsBarcodeGenerated] = useState(false);
  const [printConfirmData, setPrintConfirmData] = useState<{ item: InventoryItem; quantity: number } | null>(null);
  // Raw string state for numeric inputs so clearing-then-typing works correctly
  const [stockQtyInput, setStockQtyInput] = useState('0');
  const [minStockInput, setMinStockInput] = useState('0');

  const addDynamicCategory = async (categoryName: string) => {
    if (!currentUser || !categoryName) return;
    
    if (categories.some(c => c.name.toLowerCase() === categoryName.toLowerCase()) || 
        INVENTORY_CATEGORIES.some(c => c.toLowerCase() === categoryName.toLowerCase())) {
      return;
    }

    try {
      const categoryId = 'cat_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      await db.put('inventoryCategories', {
        id: categoryId,
        name: categoryName,
        description: 'Auto-added from barcode lookup',
        userId: currentUser.uid,
        createdAt: new Date().toISOString(),
      });
      fetchCategories();
    } catch (err) {
      console.warn('Failed to save dynamic category', err);
    }
  };

  const lookupBarcode = async (barcode: string) => {
    if (!barcode) return;
    setIsLookingUp(true);

    try {
      // Step 1: Primary Fetch (UPCitemdb)
      const upcResponse = await fetch(`https://api.upcitemdb.com/prod/trial/lookup?upc=${barcode}`);
      if (upcResponse.ok) {
        const upcData = await upcResponse.json();
        if (upcData.items && upcData.items.length > 0) {
          const item = upcData.items[0];
          const extractedCat = item.category ? item.category.split('>').pop()?.trim() || item.category : '';
          if (extractedCat) await addDynamicCategory(extractedCat);

          setNewItem(prev => ({
            ...prev,
            name: item.title || prev.name,
            category: extractedCat || prev.category,
            description: item.description || prev.description
          }));
          setIsLookingUp(false);
          return;
        }
      }
      throw new Error('UPCitemdb failed or returned no items');
    } catch (error) {
      // Step 2: Fallback Fetch (Open Food Facts)
      try {
        const offResponse = await fetch(`https://world.openfoodfacts.org/api/v0/product/${barcode}.json`);
        if (offResponse.ok) {
          const offData = await offResponse.json();
          if (offData.status === 1 && offData.product) {
            const product = offData.product;
            const extractedCat = product.categories ? product.categories.split(',')[0].trim() : '';
            if (extractedCat) await addDynamicCategory(extractedCat);
            
            setNewItem(prev => ({
              ...prev,
              name: product.product_name || prev.name,
              category: extractedCat || prev.category,
            }));
          }
        }
      } catch (fallbackError) {
        console.warn('Barcode lookup failed on all APIs', fallbackError);
      }
    } finally {
      setIsLookingUp(false);
    }
  };

  useEffect(() => {
    if (printingBarcode) {
      setTimeout(() => {
        window.print();
        setPrintingBarcode(null);
      }, 100);
    }
  }, [printingBarcode]);

  const fetchItems = useCallback(async () => {
    if (!currentUser) return;
    try {
      const fetchedItems = await db.getAll<InventoryItem>('inventory');
      const itemsData = fetchedItems.map(data => ({
        ...data,
        lowStock: (data.stockQuantity || 0) <= (data.minStockLevel || 0),
        stockValue: (data.stockQuantity || 0) * (data.purchasePrice || 0)
      }));
      setItems(itemsData);
    } catch (error) {
      console.error('Error fetching items:', error);
      setSnackbar({ open: true, message: 'Error fetching items', severity: 'error' });
    }
  }, [currentUser, db, setSnackbar]);

  const fetchCategories = useCallback(async () => {
    if (!currentUser) return;
    try {
      const fetchedCategories = await db.getAll<InventoryCategory>('inventoryCategories');
      setCategories(fetchedCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, [currentUser, db]);

  const fetchPurchases = useCallback(async () => {
    if (!currentUser) return;
    try {
      const fetchedPurchases = await db.getAll<Purchase>('purchases');
      setPurchases(fetchedPurchases);
    } catch (error) {
      console.error('Error fetching purchases:', error);
    }
  }, [currentUser, db]);

  const fetchReturns = useCallback(async () => {
    if (!currentUser) return;
    try {
      const fetchedReturns = await db.getAll<POSReturnRecord>('posReturns');
      setPosReturns(fetchedReturns);
    } catch (error) {
      console.error('Error fetching posReturns:', error);
    }
  }, [currentUser, db]);

  useEffect(() => {
    if (currentUser) {
      fetchItems();
      fetchCategories();
      fetchPurchases();
      fetchReturns();
    }
  }, [currentUser, fetchItems, fetchCategories, fetchPurchases, fetchReturns]);

  // Re-fetch after migration completes so cloud data is visible immediately
  useEffect(() => {
    if (migration.done && migration.migrated > 0) {
      fetchItems();
      fetchCategories();
      fetchPurchases();
      fetchReturns();
    }
  }, [migration.done, migration.migrated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Calculate total stock value and low stock count
    let totalValue = 0;
    let damagedValue = 0;
    let lowCount = 0;

    items.forEach(item => {
      // Calculate stock value (quantity * purchase price)
      const itemValue = item.stockQuantity * item.purchasePrice;
      totalValue += itemValue;

      // Calculate damaged value (damaged quantity * purchase price)
      const dmgValue = (item.damaged_stock || 0) * item.purchasePrice;
      damagedValue += dmgValue;

      // Check if item is low on stock
      if (item.stockQuantity <= item.minStockLevel) {
        lowCount++;
      }
    });

    setTotalStockValue(totalValue);
    setTotalDamagedValue(damagedValue);
    setLowStockCount(lowCount);
  }, [items]);

  const handleOpenDialog = (item?: InventoryItem) => {
    if (item) {
      setSelectedItem(item);
      setNewItem(item);
      setStockQtyInput(String(item.stockQuantity ?? 0));
      setMinStockInput(String(item.minStockLevel ?? 0));
    } else {
      setSelectedItem(null);
      setNewItem({
        name: '',
        description: '',
        skuWithUnit: '',
        category: '',
        purchasePrice: 0,
        sellingPrice: 0,
        gstRate: 0,
        hsnCode: '',
        stockQuantity: 0,
        minStockLevel: 0,
        purchaseId: '',
        barcode: '',
      });
      setStockQtyInput('0');
      setMinStockInput('0');
    }
    setOpenDialog(true);
    setIsBarcodeGenerated(false);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setIsBarcodeGenerated(false);
    setSelectedItem(null);
    setStockQtyInput('0');
    setMinStockInput('0');
    setNewItem({
      name: '',
      description: '',
      skuWithUnit: '',
      category: '',
      purchasePrice: 0,
      sellingPrice: 0,
      gstRate: 0,
      hsnCode: '',
      stockQuantity: 0,
      minStockLevel: 0,
      purchaseId: '',
      barcode: '',
    });
  };

  const handleSaveItem = async () => {
    if (!currentUser) return;

    try {
      const itemData = {
        ...newItem,
        purchasePrice: Number(newItem.purchasePrice) || 0,
        sellingPrice: Number(newItem.sellingPrice) || 0,
        stockQuantity: Number(stockQtyInput) || 0,
        minStockLevel: Number(minStockInput) || 0,
        userId: currentUser.uid,
        updatedAt: new Date().toISOString(),
        createdAt: selectedItem ? selectedItem.createdAt : new Date().toISOString(),
      };

      let itemId = selectedItem?.id;
      if (!itemId) {
        itemId = 'item_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      }

      await db.put('inventory', { ...itemData, id: itemId });

      if (isBarcodeGenerated) {
        setPrintConfirmData({ item: { ...itemData, id: itemId } as InventoryItem, quantity: itemData.stockQuantity || 1 });
      } else {
        setSnackbar({ open: true, message: `Item ${selectedItem ? 'updated' : 'added'} successfully`, severity: 'success' });
      }
      handleCloseDialog();
      fetchItems();
    } catch (error) {
      console.error('Error saving item:', error);
      setSnackbar({
        open: true,
        message: 'Error saving item',
        severity: 'error',
      });
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    try {
      await db.remove('inventory', itemId);
      setSnackbar({ open: true, message: 'Item deleted successfully', severity: 'success' });
      fetchItems();
    } catch (error) {
      console.error('Error deleting item:', error);
      setSnackbar({ open: true, message: 'Error deleting item', severity: 'error' });
    }
  };

  const handleOpenCategoryDialog = () => {
    setOpenCategoryDialog(true);
  };

  const handleCloseCategoryDialog = () => {
    setOpenCategoryDialog(false);
    setNewCategory({
      name: '',
      description: '',
    });
  };

  const handleSaveCategory = async () => {
    if (!currentUser || !newCategory.name) return;

    try {
      const categoryData = {
        ...newCategory,
        userId: currentUser.uid,
        createdAt: new Date().toISOString(),
      };

      const categoryId = 'cat_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      await db.put('inventoryCategories', { ...categoryData, id: categoryId });

      setSnackbar({ open: true, message: 'Category added successfully', severity: 'success' });
      handleCloseCategoryDialog();
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      setSnackbar({
        open: true,
        message: 'Error saving category',
        severity: 'error',
      });
    }
  };

  const handleOpenFilterDialog = () => {
    setOpenFilterDialog(true);
  };

  const handleCloseFilterDialog = () => {
    setOpenFilterDialog(false);
  };

  const handleApplyFilters = () => {
    setOpenFilterDialog(false);
    // Filters are already stored in state, so we just need to close the dialog
    // The filtering is applied in the filteredItems computation
  };

  const handleClearFilters = () => {
    setFilters({
      category: null,
      lowStock: false,
      dateRange: [null, null],
      priceRange: [null, null],
    });
    setSearchQuery('');
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Convert camelCase to Title Case with spaces
  const formatColumnName = (name: string) => {
    return name
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase());
  };

  // Export filtered items or returns to CSV
  const exportToCSV = () => {
    const isReturnsTab = tabValue === 2 || tabValue === 3;
    const itemsToExport = tabValue === 0 ? filteredItems 
                        : tabValue === 1 ? lowStockItems 
                        : tabValue === 2 ? returnedRecords 
                        : damagedRecords;

    if (itemsToExport.length === 0) {
      setSnackbar({
        open: true,
        message: 'No records to export in this tab',
        severity: 'error',
      });
      return;
    }

    // Define columns based on tab type
    const inventoryColumns = [
      'name',
      'description',
      'skuWithUnit',
      'category',
      'purchasePrice',
      'sellingPrice',
      'gstRate',
      'hsnCode',
      'stockQuantity',
      'minStockLevel',
      'returned_stock',
      'damaged_stock',
      'last_return_reason',
      'stockValue',
      'updatedAt',
    ];
    
    const returnsColumns = [
      'date',
      'productName',
      'clientName',
      'reason',
      'quantity',
      'totalValue',
      'billNumber',
      'condition',
    ];

    const columns = isReturnsTab ? returnsColumns : inventoryColumns;

    // Create the CSV header row
    const header = columns.map(formatColumnName).join(',');

    // Create CSV content with rows
    const csvContent = itemsToExport.map((item: any) => {
      return columns.map(col => {
        const value = item[col];
        // Format values and handle special cases
        if (col === 'updatedAt' && typeof value === 'string') {
          return `"${new Date(value).toLocaleDateString()}"`;
        }
        if (typeof value === 'string' && value.includes(',')) {
          return `"${value}"`;
        }
        return value;
      }).join(',');
    }).join('\n');

    // Create the full CSV content with header
    const fullContent = `${header}\n${csvContent}`;

    // Create a blob and download link
    const blob = new Blob([fullContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `inventory_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Get purchase information by ID
  const getPurchaseInfo = (purchaseId: string | undefined) => {
    if (!purchaseId) return 'Not linked';
    const purchase = purchases.find(p => p.id === purchaseId);
    return purchase ? `${purchase.invoiceNumber} (${new Date(purchase.date).toLocaleDateString()})` : 'Unknown';
  };

  // Filter items based on search query and filters
  const filteredItems = items.filter(item => {
    // Text search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (
        !item.name.toLowerCase().includes(query) &&
        !item.skuWithUnit.toLowerCase().includes(query) &&
        !item.category.toLowerCase().includes(query) &&
        !item.description?.toLowerCase().includes(query)
      ) {
        return false;
      }
    }

    // Category filter
    if (filters.category && item.category !== filters.category) {
      return false;
    }

    // Low stock filter
    if (filters.lowStock && !item.lowStock) {
      return false;
    }

    // Date range filter (based on updatedAt)
    if (filters.dateRange[0] && new Date(item.updatedAt) < filters.dateRange[0]) {
      return false;
    }
    if (filters.dateRange[1]) {
      const endDate = new Date(filters.dateRange[1]);
      endDate.setHours(23, 59, 59, 999); // Set to end of day
      if (new Date(item.updatedAt) > endDate) {
        return false;
      }
    }

    // Price range filter
    if (filters.priceRange[0] !== null && item.purchasePrice < filters.priceRange[0]) {
      return false;
    }
    if (filters.priceRange[1] !== null && item.purchasePrice > filters.priceRange[1]) {
      return false;
    }

    return true;
  });

  // Group items by whether they are low stock (for the tab view)
  const lowStockItems = filteredItems.filter(item => item.lowStock);
  
  // Filter return records
  const filterReturnSearch = (r: POSReturnRecord) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return r.productName.toLowerCase().includes(q) || r.clientName.toLowerCase().includes(q) || r.billNumber.toLowerCase().includes(q);
  };
  const returnedRecords = posReturns.filter(r => r.condition === 'restock' && filterReturnSearch(r));
  const damagedRecords = posReturns.filter(r => r.condition === 'writeoff' && filterReturnSearch(r));

  return (
    <AnimatedPage>
      <Box sx={{ p: 3 }}>
        {/* Migration status banner — shown only during first-time cloud sync */}
        {migration.running && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }} icon={<CircularProgress size={18} />}>
            Syncing your local data to the workspace cloud… please wait.
          </Alert>
        )}
        {migration.done && migration.migrated > 0 && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            ✅ {migration.migrated} item{migration.migrated !== 1 ? 's' : ''} synced to workspace cloud — now visible to all staff.
          </Alert>
        )}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" sx={{
            background: 'linear-gradient(45deg, #673AB7 30%, #9C27B0 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 'bold'
          }}>
            Inventory Management
          </Typography>

          <Box display="flex" gap={2}>
            <Tooltip title="Search items">
              <IconButton
                size="medium"
                onClick={() => setSearchQuery(searchQuery ? '' : ' ')}
                color="primary"
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
              >
                <SearchIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Filter items">
              <IconButton
                size="medium"
                onClick={handleOpenFilterDialog}
                color="primary"
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
              >
                <FilterListIcon />
              </IconButton>
            </Tooltip>

            <Button
              variant="outlined"
              startIcon={<CategoryIcon />}
              onClick={handleOpenCategoryDialog}
            >
              Add Category
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                background: 'linear-gradient(45deg, #673AB7 30%, #9C27B0 90%)',
                color: 'white',
              }}
            >
              Add Item
            </Button>
          </Box>
        </Box>

        {/* Conditional Search Box - Only visible when search is activated */}
        {searchQuery !== '' && (
          <Box sx={{ mb: 3 }}>
            <TextField
              placeholder="Search items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              variant="outlined"
              fullWidth
              size="small"
            />
          </Box>
        )}

        {/* Summary Cards */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 4,
                background: 'linear-gradient(135deg, rgba(103, 58, 183, 0.15) 0%, rgba(156, 39, 176, 0.05) 100%)',
                border: '1px solid rgba(103, 58, 183, 0.1)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                '&:hover': {
                  boxShadow: '0 8px 25px rgba(103, 58, 183, 0.15)',
                  transform: 'translateY(-5px)',
                }
              }}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(103, 58, 183, 0.2)',
                    color: '#673AB7',
                    mr: 2
                  }}
                >
                  <InventoryIcon fontSize="small" />
                </Box>
                <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                  Total Items
                </Typography>
              </Box>

              <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#673AB7' }}>
                {items.length}
              </Typography>
            </Paper>
          </Grid>

          {!isCashier && (
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 4,
                background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.15) 0%, rgba(233, 30, 99, 0.05) 100%)',
                border: '1px solid rgba(156, 39, 176, 0.1)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                '&:hover': {
                  boxShadow: '0 8px 25px rgba(156, 39, 176, 0.15)',
                  transform: 'translateY(-5px)',
                }
              }}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(156, 39, 176, 0.2)',
                    color: '#9C27B0',
                    mr: 2
                  }}
                >
                  <PriceCheckIcon fontSize="small" />
                </Box>
                <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                  Total Stock Value
                </Typography>
              </Box>

              <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#9C27B0' }}>
                ₹{totalStockValue.toLocaleString()}
              </Typography>
            </Paper>
          </Grid>
          )}

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 4,
                background: 'linear-gradient(135deg, rgba(233, 30, 99, 0.15) 0%, rgba(244, 67, 54, 0.05) 100%)',
                border: '1px solid rgba(233, 30, 99, 0.1)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                '&:hover': {
                  boxShadow: '0 8px 25px rgba(233, 30, 99, 0.15)',
                  transform: 'translateY(-5px)',
                }
              }}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(233, 30, 99, 0.2)',
                    color: '#E91E63',
                    mr: 2
                  }}
                >
                  <WarningIcon fontSize="small" />
                </Box>
                <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                  Low Stock Items
                </Typography>
              </Box>

              <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#E91E63' }}>
                {lowStockCount}
              </Typography>
            </Paper>
          </Grid>

          {!isCashier && (
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 4,
                background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.15) 0%, rgba(255, 87, 34, 0.05) 100%)',
                border: '1px solid rgba(255, 152, 0, 0.1)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                '&:hover': {
                  boxShadow: '0 8px 25px rgba(255, 152, 0, 0.15)',
                  transform: 'translateY(-5px)',
                }
              }}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 152, 0, 0.2)',
                    color: '#FF9800',
                    mr: 2
                  }}
                >
                  <WarningIcon fontSize="small" />
                </Box>
                <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                  Damaged Value
                </Typography>
              </Box>

              <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#FF9800' }}>
                ₹{totalDamagedValue.toLocaleString()}
              </Typography>
            </Paper>
          </Grid>
          )}
        </Grid>

        {/* Tabs and Actions Row */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <Box display="flex" alignItems="center">
            <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="All Items" />
            <Tab
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <span>Low Stock</span>
                  {lowStockItems.length > 0 && (
                    <Box
                      sx={{
                        backgroundColor: '#EF4444',
                        color: 'white',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        borderRadius: '50%',
                        minWidth: '18px',
                        height: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 0.5
                      }}
                    >
                      {lowStockItems.length}
                    </Box>
                  )}
                </Box>
              }
            />
            <Tab
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <span>Returned (Good)</span>
                  {returnedRecords.length > 0 && (
                    <Box
                      sx={{
                        backgroundColor: '#3B82F6',
                        color: 'white',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        borderRadius: '50%',
                        minWidth: '18px',
                        height: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 0.5
                      }}
                    >
                      {returnedRecords.length}
                    </Box>
                  )}
                </Box>
              }
            />
            <Tab
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <span>Damaged</span>
                  {damagedRecords.length > 0 && (
                    <Box
                      sx={{
                        backgroundColor: '#F97316',
                        color: 'white',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        borderRadius: '50%',
                        minWidth: '18px',
                        height: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 0.5
                      }}
                    >
                      {damagedRecords.length}
                    </Box>
                  )}
                </Box>
              }
            />
          </Tabs>
          </Box>
          
          <Box display="flex" gap={2} mb={1}>
            {(searchQuery || filters.category || filters.lowStock ||
              filters.dateRange[0] || filters.dateRange[1] ||
              filters.priceRange[0] || filters.priceRange[1]) && (
                <Button
                  variant="text"
                  onClick={handleClearFilters}
                  size="small"
                >
                  Clear Filters
                </Button>
              )}
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportToCSV}
              size="small"
            >
              Export to CSV
            </Button>
          </Box>
        </Box>

        {/* Table */}
        <TableContainer component={Paper} elevation={0} sx={{
          borderRadius: 2,
          boxShadow: '0 4px 20px 0 rgba(0,0,0,0.14)',
          overflowX: 'auto',
          width: '100%'
        }}>
          <Table>
            {tabValue === 0 || tabValue === 1 ? (
              <>
                <TableHead>
                  <TableRow sx={{ background: 'linear-gradient(45deg, #673AB7 30%, #9C27B0 90%)' }}>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>SKU/Unit</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Category</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Stock</TableCell>
                    {!isCashier && <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Purchase Price</TableCell>}
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Selling Price</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>GST Rate</TableCell>
                    {!isCashier && <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Stock Value</TableCell>}
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Purchase Ref</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(tabValue === 0 ? filteredItems : lowStockItems).map((item) => (
                    <TableRow
                      key={item.id}
                      sx={{
                        backgroundColor: item.lowStock ? 'rgba(233, 30, 99, 0.05)' : 'inherit'
                      }}
                    >
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.skuWithUnit}</TableCell>
                      <TableCell>{item.category}</TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          {item.lowStock ? (
                            <Chip
                              label={`${item.stockQuantity} (Low)`}
                              color="error"
                              size="small"
                            />
                          ) : (
                            <Typography variant="body2">{item.stockQuantity}</Typography>
                          )}
                        </Box>
                      </TableCell>
                      {!isCashier && <TableCell>₹{item.purchasePrice?.toLocaleString()}</TableCell>}
                      <TableCell>₹{item.sellingPrice?.toLocaleString()}</TableCell>
                      <TableCell>{item.gstRate}%</TableCell>
                      {!isCashier && <TableCell>₹{(item.stockQuantity * item.purchasePrice).toLocaleString()}</TableCell>}
                      <TableCell>{getPurchaseInfo(item.purchaseId)}</TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <IconButton size="small" onClick={() => handleOpenDialog(item)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDeleteItem(item.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          <Tooltip title="Print Label">
                            <IconButton size="small" onClick={() => {
                              if (!item.barcode) {
                                setSnackbar({ open: true, message: 'Item has no barcode. Edit item and generate one first.', severity: 'warning' });
                                return;
                              }
                              setPrintConfirmData({ item, quantity: item.stockQuantity || 1 });
                            }}>
                              <Printer size={18} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(tabValue === 0 ? filteredItems : lowStockItems).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isCashier ? 8 : 10} align="center" sx={{ py: 3 }}>
                        <Typography variant="subtitle1" color="text.secondary">
                          No items found matching your criteria
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </>
            ) : (
              <>
                <TableHead>
                  <TableRow sx={{ background: 'linear-gradient(45deg, #673AB7 30%, #9C27B0 90%)' }}>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Product</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Customer</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Reason</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Qty</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Total Value</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Bill #</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(tabValue === 2 ? returnedRecords : damagedRecords).map((record) => (
                    <TableRow key={record.id}>
                      <TableCell>{new Date(record.date).toLocaleDateString()}</TableCell>
                      <TableCell>{record.productName}</TableCell>
                      <TableCell>{record.clientName}</TableCell>
                      <TableCell>{record.reason}</TableCell>
                      <TableCell>
                        <Chip 
                          label={record.quantity} 
                          size="small" 
                          sx={{ 
                            bgcolor: record.condition === 'restock' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(211, 47, 47, 0.1)', 
                            color: record.condition === 'restock' ? '#1d4ed8' : '#b71c1c', 
                            fontWeight: 'bold',
                            height: 20
                          }} 
                        />
                      </TableCell>
                      <TableCell>₹{record.totalValue.toLocaleString()}</TableCell>
                      <TableCell>{record.billNumber}</TableCell>
                    </TableRow>
                  ))}
                  {(tabValue === 2 ? returnedRecords : damagedRecords).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        <Typography variant="subtitle1" color="text.secondary">
                          No return records found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </>
            )}
          </Table>
        </TableContainer>

        {/* Add/Edit Item Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {selectedItem ? 'Edit Item' : 'Add New Item'}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Name"
                  value={newItem.name}
                  onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Barcode"
                  value={newItem.barcode || ''}
                  onChange={(e) => {
                    setNewItem({ ...newItem, barcode: e.target.value });
                    setIsBarcodeGenerated(false);
                  }}
                  fullWidth
                  margin="normal"
                  placeholder="Click here and scan item"
                  helperText="Press Enter to auto-lookup details"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      lookupBarcode(newItem.barcode || '');
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {isLookingUp ? (
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                        ) : (
                          <Tooltip title="Lookup Barcode">
                            <IconButton onClick={() => lookupBarcode(newItem.barcode || '')}>
                              <SearchIconLucide size={20} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Generate Unique Barcode">
                          <IconButton onClick={() => {
                            const rawName = userProfile?.businessName || '';
                            const cleanName = rawName.replace(/[^A-Za-z]/g, '');
                            const prefix = cleanName.length > 0 ? cleanName.substring(0, 4).toUpperCase() : 'AKMO';
                            const uniqueCode = `${prefix}-${Date.now().toString(36).toUpperCase()}`;
                            setNewItem({ ...newItem, barcode: uniqueCode });
                            setIsBarcodeGenerated(true);
                          }}>
                            <Wand2 size={20} />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>SKU with Unit</InputLabel>
                  <Select
                    value={newItem.skuWithUnit?.split('-')[0] || ''}
                    label="SKU with Unit"
                    onChange={(e) => {
                      const prefix = e.target.value;
                      const suffix = newItem.skuWithUnit?.split('-')[1] || '';
                      setNewItem({ ...newItem, skuWithUnit: `${prefix}${suffix}` });
                    }}
                    startAdornment={
                      <InputAdornment position="start">
                        <TextField
                          variant="standard"
                          placeholder="SKU"
                          value={newItem.skuWithUnit?.split('-')[1] || ''}
                          onChange={(e) => {
                            const prefix = newItem.skuWithUnit?.split('-')[0] || '';
                            const suffix = e.target.value;
                            setNewItem({ ...newItem, skuWithUnit: `${prefix}${suffix}` });
                          }}
                          sx={{ width: '100px', mr: 1, border: 'none' }}
                          InputProps={{ disableUnderline: true }}
                        />
                      </InputAdornment>
                    }
                  >
                    {UNITS.map((unit) => (
                      <MenuItem key={unit} value={unit}>
                        {unit}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={newItem.category}
                    label="Category"
                    onChange={(e) => {
                      if (e.target.value === 'ADD_NEW_CATEGORY') {
                        handleOpenCategoryDialog();
                      } else {
                        setNewItem({ ...newItem, category: e.target.value });
                      }
                    }}
                  >
                    <MenuItem value="ADD_NEW_CATEGORY" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <AddIcon fontSize="small" /> Add New Category
                      </Box>
                    </MenuItem>
                    <Divider />
                    {categories.length > 0 ? (
                      categories.map((category) => (
                        <MenuItem key={category.id} value={category.name}>
                          {category.name}
                        </MenuItem>
                      ))
                    ) : (
                      INVENTORY_CATEGORIES.map((category) => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))
                    )}
                    {/* Handle dynamic category from API lookup just in case it hasn't re-rendered yet */}
                    {newItem.category && 
                     !categories.some(c => c.name === newItem.category) && 
                     !INVENTORY_CATEGORIES.includes(newItem.category) && (
                      <MenuItem value={newItem.category}>
                        {newItem.category}
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Purchase Reference</InputLabel>
                  <Select
                    value={newItem.purchaseId || ''}
                    label="Purchase Reference"
                    onChange={(e) => setNewItem({ ...newItem, purchaseId: e.target.value })}
                  >
                    <MenuItem value="">No Purchase Reference</MenuItem>
                    {purchases.map((purchase) => (
                      <MenuItem key={purchase.id} value={purchase.id}>
                        {purchase.invoiceNumber} ({new Date(purchase.date).toLocaleDateString()})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={newItem.description}
                  onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                  fullWidth
                  multiline
                  rows={2}
                  margin="normal"
                />
              </Grid>
              {!isCashier && (
              <Grid item xs={12} md={6}>
                <TextField
                  label="Purchase Price"
                  type="number"
                  value={newItem.purchasePrice === 0 ? '' : newItem.purchasePrice}
                  onChange={(e) => setNewItem({ ...newItem, purchasePrice: e.target.value === '' ? 0 : Number(e.target.value) })}
                  fullWidth
                  margin="normal"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                />
              </Grid>
              )}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Selling Price"
                  type="number"
                  value={newItem.sellingPrice === 0 ? '' : newItem.sellingPrice}
                  onChange={(e) => setNewItem({ ...newItem, sellingPrice: e.target.value === '' ? 0 : Number(e.target.value) })}
                  fullWidth
                  margin="normal"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>GST Rate</InputLabel>
                  <Select
                    value={newItem.gstRate}
                    label="GST Rate"
                    onChange={(e) => setNewItem({ ...newItem, gstRate: Number(e.target.value) })}
                  >
                    {GST_RATES.map((rate) => (
                      <MenuItem key={rate.value} value={rate.value}>
                        {rate.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="HSN Code"
                  value={newItem.hsnCode}
                  onChange={(e) => setNewItem({ ...newItem, hsnCode: e.target.value })}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Stock Quantity"
                  type="number"
                  value={stockQtyInput}
                  onChange={(e) => setStockQtyInput(e.target.value)}
                  onFocus={(e) => { if (e.target.value === '0') setStockQtyInput(''); }}
                  onBlur={(e) => { if (e.target.value === '') setStockQtyInput('0'); }}
                  fullWidth
                  margin="normal"
                  helperText="Current units in stock"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Minimum Stock Level"
                  type="number"
                  value={minStockInput}
                  onChange={(e) => setMinStockInput(e.target.value)}
                  onFocus={(e) => { if (e.target.value === '0') setMinStockInput(''); }}
                  onBlur={(e) => { if (e.target.value === '') setMinStockInput('0'); }}
                  fullWidth
                  margin="normal"
                  helperText="Alert threshold (0 = only alert when out of stock)"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              onClick={handleSaveItem}
              variant="contained"
              sx={{
                backgroundImage: 'var(--primary-gradient)',
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Category Dialog */}
        <Dialog open={openCategoryDialog} onClose={handleCloseCategoryDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            Add New Category
          </DialogTitle>
          <DialogContent dividers>
            <Box display="flex" flexDirection="column" gap={2} p={1}>
              <TextField
                label="Category Name"
                value={newCategory.name}
                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                fullWidth
              />
              <TextField
                label="Description"
                value={newCategory.description}
                onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCategoryDialog}>Cancel</Button>
            <Button
              onClick={handleSaveCategory}
              variant="contained"
              sx={{
                backgroundImage: 'var(--primary-gradient)',
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Filter Dialog */}
        <Dialog open={openFilterDialog} onClose={handleCloseFilterDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            Filter Inventory
          </DialogTitle>
          <DialogContent dividers>
            <Box display="flex" flexDirection="column" gap={3} p={1}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filters.category || ''}
                  label="Category"
                  onChange={(e) => setFilters({ ...filters, category: e.target.value || null })}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.length > 0 ? (
                    categories.map((category) => (
                      <MenuItem key={category.id} value={category.name}>
                        {category.name}
                      </MenuItem>
                    ))
                  ) : (
                    INVENTORY_CATEGORIES.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={filters.lowStock}
                    onChange={(e) => setFilters({ ...filters, lowStock: e.target.checked })}
                  />
                }
                label="Show only low stock items"
              />

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Purchase Price Range
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="Min"
                      type="number"
                      fullWidth
                      value={filters.priceRange[0] || ''}
                      onChange={(e) => {
                        const value = e.target.value === '' ? null : Number(e.target.value);
                        setFilters({
                          ...filters,
                          priceRange: [value, filters.priceRange[1]],
                        });
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Max"
                      type="number"
                      fullWidth
                      value={filters.priceRange[1] || ''}
                      onChange={(e) => {
                        const value = e.target.value === '' ? null : Number(e.target.value);
                        setFilters({
                          ...filters,
                          priceRange: [filters.priceRange[0], value],
                        });
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Last Updated Date Range
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <DatePicker
                      label="From"
                      value={filters.dateRange[0]}
                      onChange={(date) => setFilters({
                        ...filters,
                        dateRange: [date, filters.dateRange[1]],
                      })}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DatePicker
                      label="To"
                      value={filters.dateRange[1]}
                      onChange={(date) => setFilters({
                        ...filters,
                        dateRange: [filters.dateRange[0], date],
                      })}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseFilterDialog}>Cancel</Button>
            <Button onClick={handleClearFilters}>Clear All</Button>
            <Button
              onClick={handleApplyFilters}
              variant="contained"
              sx={{
                backgroundImage: 'var(--primary-gradient)',
              }}
            >
              Apply Filters
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
        
        {/* Print Confirmation Dialog */}
        <Dialog open={!!printConfirmData} onClose={() => setPrintConfirmData(null)}>
          <DialogTitle>Print Barcode Labels</DialogTitle>
          <DialogContent>
            <Typography gutterBottom mt={1}>
              You just generated a new barcode for <strong>{printConfirmData?.item.name}</strong>.
            </Typography>
            <Typography>
              Would you like to print <strong>{printConfirmData?.quantity}</strong> labels (50x25mm) for your stock now?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button onClick={() => setPrintConfirmData(null)}>Skip</Button>
            <Button 
              variant="contained" 
              onClick={() => {
                if (printConfirmData) {
                  setPrintingQuantity(printConfirmData.quantity || 1);
                  setPrintingBarcode(printConfirmData.item);
                  setPrintConfirmData(null);
                }
              }}
              sx={{ backgroundImage: 'var(--primary-gradient)' }}
            >
              Print Labels
            </Button>
          </DialogActions>
        </Dialog>

        {printingBarcode && (
          <Box id="print-barcode-container">
            <style>
              {`
                @media print {
                  body * {
                    visibility: hidden;
                  }
                  #print-barcode-container, #print-barcode-container * {
                    visibility: visible;
                  }
                  #print-barcode-container {
                    position: absolute;
                    left: 0;
                    top: 0;
                    margin: 0;
                    padding: 0;
                  }
                  .sticker-page {
                    page-break-after: always;
                    break-after: page;
                  }
                }
              `}
            </style>
            {Array.from({ length: printingQuantity }).map((_, idx) => (
              <Box key={idx} className="sticker-page">
                <BarcodeSticker 
                  productName={printingBarcode.name} 
                  barcode={printingBarcode.barcode!} 
                  price={printingBarcode.sellingPrice} 
                  tenantName={userProfile?.businessName || "AKMO BOOKS"}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </AnimatedPage>
  );
};

export default Inventory; 
